import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import CheerCard from '../../components/common/CheerCard';
import { apiRequest, apiFormRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyFeed.css';

/* The company's activity stream — F4.

   What has been going on here, in one place: what colleagues wrote, the pictures from last week's
   townhall, the host's recap, the event that was just announced. Four producers, one list.

   ★ Everybody writes; the admin moderates. ``can_moderate`` arrives with the payload and only
   decides what is worth drawing — every write is re-checked server-side, so a hidden button is a
   convenience and never the guard.

   ★ The stream is already per viewer when it arrives. Cards from an event are only in the payload
   if this person may see that event; the screen never filters, because a card it received and
   declined to draw would already have been published.

   ★ A card is a POINTER. Liking a photo here writes the same row liking it on the event page
   does, and a comment lands in that event's own thread — one conversation, two screens. */

const MAX_BODY = 4000;
/* The stream pages on a cursor, the same shape the mission feed uses. */
const PAGE = 20;
/* The same six the event page offers, and for the same reason: the queue in Machina triages on
   the reason, and asking an upset person to phrase the category themselves gets you a hundred
   spellings of one thing. Kept in step with routes/company_feed's REASONS by the server, which
   folds anything it does not know into 'other'. */
const REPORT_REASONS = ['abuse', 'harassment', 'sexual', 'spam', 'privacy', 'other'];

const Avatar = ({ url, name }) => (
  url
    ? <img className="cf-avatar" src={url} alt="" />
    : <span className="cf-avatar cf-avatar--empty">{(name || '?').trim().charAt(0) || '?'}</span>
);

/* ``clubId`` turns this into ONE CLUB's stream, drawn inside the club's page: every call carries
   ``club=<id>`` (routes/company_feed reads it on every route, writes included), the page chrome and
   the company/Labs scope chips stay off, and the server pins the scope to the club's own cards. */
const CompanyFeed = ({ clubId = null } = {}) => {
  const { t, i18n } = useTranslation();
  const at = useCallback((path) => (clubId
    ? `${path}${path.includes('?') ? '&' : '?'}club=${encodeURIComponent(clubId)}`
    : path), [clubId]);
  const Frame = clubId ? React.Fragment : Base;
  const frameProps = clubId ? {} : { activeNav: 'feed' };
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [more, setMore] = useState([]);        // pages fetched after the first
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [scope, setScope] = useState(null);    // null | 'labs' | 'company'
  const [eventId, setEventId] = useState(null);
  const [reporting, setReporting] = useState(null);   // card being objected to
  const [reportReason, setReportReason] = useState('abuse');
  const [reportNote, setReportNote] = useState('');
  /* What the post being written CARRIES: a picture, and one thing it points at
     ({kind:'survey'|'event', id}). ``attaching`` is only which picker is open. */
  const [photo, setPhoto] = useState(null);
  const [link, setLink] = useState(null);
  const [attaching, setAttaching] = useState(null);

  /* The filters live in the URL the server reads, never in a client-side .filter(): the stream
     arrives already narrowed to what this person may see, and re-filtering it here would only be
     able to hide things, never to reach the next page of what is left. */
  const query = useCallback((extra) => {
    const q = [`limit=${PAGE}`];
    if (scope) q.push(`scope=${scope}`);
    if (eventId) q.push(`event=${encodeURIComponent(eventId)}`);
    if (extra) q.push(extra);
    return q.join('&');
  }, [scope, eventId]);

  const load = useCallback(() => {
    apiRequest('GET', at(`/company/feed?${query()}`))
      .then((res) => {
        setData(res);
        setMore([]);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
      })
      .catch(() => (clubId ? setData({ items: [] }) : navigate('/member-home')));
  }, [navigate, query, at, clubId]);

  useEffect(() => { load(); }, [load]);

  /* Every write answers with a fresh FIRST page, so the pages fetched after it are dropped: they
     were a window on a list that has just changed underneath them, and keeping them would show
     the same card twice. */
  const act = (promise) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return promise
      .then((res) => {
        setData(res);
        setMore([]);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
        setReplyTo(null);
        setReplyDraft('');
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Filing an objection. Deliberately NOT routed through ``act``: that helper exists for writes
     that change the stream, and this one changes nothing — which is the very thing the person has
     to be told, so the answer is a sentence rather than a redraw. */
  const sendReport = (cardId) => {
    if (busy) return;
    setReporting(null);
    setBusy(true);
    setNotice(null);
    apiRequest('POST', at(`/company/feed/${cardId}/report`),
               { reason: reportReason, note: reportNote.trim() })
      .then(() => { setReportNote(''); setNotice(t('reportThanks')); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const loadMore = () => {
    if (busy || !cursor) return;
    setBusy(true);
    apiRequest('GET', at(`/company/feed?${query(`cursor=${encodeURIComponent(cursor)}`)}`))
      .then((res) => {
        setMore((pages) => [...pages, ...(res.items || [])]);
        setCursor(res.next_cursor);
        setHasMore(!!res.has_more);
      })
      .catch(() => setNotice(t('saveFailed')))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return clubId ? <span className="auth__spinner" /> : (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const me = data.me;
  const canModerate = !!data.can_moderate;
  // Absent means yes: an older payload must not silence a whole company's feed.
  const canPost = data.can_post !== false;
  const feedEvents = data.events || [];
  /* What the composer may attach, sent with the first page: the events this person may point at
     and their company's open question sets. Absent means the screen simply offers neither — an
     older payload must not break the box people write in. */
  const targets = data.post_targets || {};
  const targetEvents = targets.events || [];
  const targetSurveys = targets.surveys || [];
  const hasCompany = !!data.has_company;
  const pinned = data.pinned || [];
  const items = [...(data.items || []), ...more];

  /* Who may take a card off the stream.

     ★ An announcement is the ADMIN's to remove and nobody else's: it carries no author, so the
     "my own card" half below could never reach it, and the card is not the event — taking it off
     leaves the event, its page and its registrations exactly where they are (cancelling one is
     its own button, on the event). Company-less Lab cards are left alone for the same reason
     pinning refuses them: there is no admin whose editorial call it would be. */
  const canRemove = (card) => (card.kind === 'event_published'
    ? canModerate && !!card.company_id
    : canModerate || card.author_id === me);

  /* One write, two shapes on the wire: a picture makes it multipart, everything else stays JSON.
     Same route either way — it is the same post, and a second endpoint would be a second set of
     rules about who may write here. A picture or a link is enough on its own: a photograph with
     no caption is a post, an empty box is a mistake. */
  const post = () => {
    const body = draft.trim().slice(0, MAX_BODY);
    if (!body && !photo && !link) return;
    const done = () => {
      setDraft('');
      setPhoto(null);
      setLink(null);
      setAttaching(null);
    };
    if (photo) {
      const form = new FormData();
      form.append('file', photo);
      if (body) form.append('body', body);
      if (link) { form.append('link_type', link.kind); form.append('link_id', link.id); }
      act(apiFormRequest(at('/company/feed'), form)).then(done);
      return;
    }
    act(apiRequest('POST', at('/company/feed'), {
      body,
      link_type: link ? link.kind : '',
      link_id: link ? link.id : '',
    })).then(done);
  };

  const comment = (card) => {
    const body = replyDraft.trim();
    if (!body) return;
    act(apiRequest('POST', at(`/company/feed/${card.id}/comment`),
                   { body: body.slice(0, MAX_BODY) }));
  };

  /* What a card is ABOUT, in one line above it — the reason a photo from Thursday's townhall does
     not read as a picture from nowhere. */
  const cardLead = (card) => {
    if (card.kind === 'event_published') return null;
    /* A cheer names its own two people inside the card, in both faces and words. A lead line
       above it saying which evening it happened on would be the wrong fact: recognition is about
       a person, not about an occasion. */
    if (card.kind === 'cheer') return null;
    /* A mission answer says which mission it answers, and that beats naming the event it hung
       off: "Take a photo of your desk" is what makes the picture legible, and a mission is just
       as often company-wide, with no event to name at all. */
    if (card.kind === 'mission' && card.mission) {
      return (
        <div className="cf-card__lead">
          <Icon name="flag" className="me-1" />
          {[card.mission.mission_name, card.event ? card.event.name : null]
            .filter(Boolean).join(' · ')}
        </div>
      );
    }
    if (!card.event) return null;
    return (
      <div className="cf-card__lead">
        <Icon name="calendar" className="me-1" />
        {card.event.name}
      </div>
    );
  };

  const cardBody = (card) => {
    if (card.kind === 'event_published' && card.announced) {
      return (
        <button
          type="button"
          className="cf-announce"
          onClick={() => navigate(`/public-event/${card.announced.id}`)}
        >
          {card.announced.image_url && (
            <img className="cf-announce__img" src={card.announced.image_url} alt="" />
          )}
          <span className="cf-announce__body">
            <span className="cf-announce__kicker">{t('feedNewEvent')}</span>
            <span className="cf-announce__name">{card.announced.name}</span>
            {card.announced.date && (
              <span className="cf-announce__when">
                {shortDate(card.announced.date, i18n.language)}
              </span>
            )}
          </span>
        </button>
      );
    }
    if (card.subject_type === 'photo' && card.photo) {
      return (
        <div className="cf-photo">
          <img className="cf-photo__img" src={card.photo.url} alt="" />
        </div>
      );
    }
    /* A cheer (F7), drawn by the SAME component the inbox and the profile use — three renderings
       of one thing drift, and the one nobody checks is the one that goes wrong. It carries its own
       two faces, so it takes no lead line and no author strip above it. */
    if (card.subject_type === 'recognition' && card.cheer) {
      return <CheerCard cheer={card.cheer} className="cf-cheer" />;
    }
    if (card.subject_type === 'mission' && card.mission) {
      const answer = card.mission;
      return (
        <div className="cf-photo">
          {answer.kind === 'picture' ? (
            <img className="cf-photo__img" src={answer.answer} alt="" />
          ) : (
            <a className="cf-mission__link" href={answer.answer}
               target="_blank" rel="noreferrer">
              <Icon name="link" className="me-1" />{t('missionOpenPost')}
            </a>
          )}
          {answer.caption && <p className="cf-card__text">{answer.caption}</p>}
        </div>
      );
    }
    if (card.post) {
      return (
        <>
          {card.post.body && <p className="cf-card__text">{card.post.body}</p>}
          {card.post.image_url && (
            <div className="cf-photo">
              <img className="cf-photo__img" src={card.post.image_url} alt="" />
            </div>
          )}
          {postLink(card.post.link)}
        </>
      );
    }
    return null;
  };

  /* What a post POINTS AT, under the words — drawn as the announcement is, because it makes the
     same promise: press it and you are there. The server sends it hydrated (a survey that has
     been unpublished, or an event that has been deleted, arrives as null) so a card never draws
     a button that goes nowhere. */
  const postLink = (target) => {
    if (!target) return null;
    const survey = target.kind === 'survey';
    return (
      <button
        type="button"
        className="cf-announce"
        onClick={() => navigate(survey ? `/f/${target.slug}` : `/public-event/${target.id}`)}
      >
        {!survey && target.image_url && (
          <img className="cf-announce__img" src={target.image_url} alt="" />
        )}
        <span className="cf-announce__body">
          <span className="cf-announce__kicker">
            {survey ? t('feedSurveyKicker') : t('feedEventKicker')}
          </span>
          <span className="cf-announce__name">{target.name}</span>
          {!survey && target.date && (
            <span className="cf-announce__when">{shortDate(target.date, i18n.language)}</span>
          )}
        </span>
      </button>
    );
  };

  const social = (card) => {
    /* Three shapes, one set of buttons. A mission answer carries its own likes and comments
       exactly as a photo does — and on a mission they are worth something to whoever handed it
       in, which is why an approved mission is on this stream at all. */
    const thing = { photo: card.photo, mission: card.mission,
                    recognition: card.cheer }[card.subject_type] || card.post;
    if (!thing) return null;
    const comments = thing.comments || [];
    return (
      <>
        <div className="cf-card__acts">
          <button
            type="button"
            className={`cf-act ${thing.liked ? 'is-on' : ''}`}
            disabled={busy}
            onClick={() => act(apiRequest('POST', at(`/company/feed/${card.id}/like`)))}
            aria-label={t('like')}
            title={t('like')}
          >
            <Icon name="heart" /> {thing.likes > 0 ? thing.likes : ''}
          </button>
          <button
            type="button"
            className="cf-act"
            disabled={busy}
            onClick={() => {
              setReplyTo(replyTo === card.id ? null : card.id);
              setReplyDraft('');
            }}
            aria-label={t('comment')}
            title={t('comment')}
          >
            <Icon name="comment" /> {comments.length > 0 ? comments.length : ''}
          </button>
        </div>

        {comments.map((c) => (
          <div key={c.id} className="cf-comment">
            <Avatar url={c.author_photo} name={c.author} />
            <div className="cf-comment__body">
              <span className="cf-comment__who">{c.author}</span>
              <span className="cf-comment__text">{c.body}</span>
            </div>
          </div>
        ))}

        {replyTo === card.id && (
          <div className="cf-reply">
            <textarea
              className="cf-input"
              rows={2}
              maxLength={MAX_BODY}
              value={replyDraft}
              placeholder={t('feedCommentPlaceholder')}
              onChange={(e) => setReplyDraft(e.target.value)}
            />
            <button
              type="button"
              className="bravos-submit"
              disabled={busy || !replyDraft.trim()}
              onClick={() => comment(card)}
            >
              {t('send')}
            </button>
          </div>
        )}
      </>
    );
  };

  const renderCard = (card) => (
    <Card key={card.id} className={`cf-card ${card.pinned ? 'is-pinned' : ''}`}>
      {card.pinned && (
        <div className="cf-card__pin">
          <Icon name="thumbtack" className="me-1" />{t('feedPinned')}
        </div>
      )}
      {cardLead(card)}

      <div className="cf-card__head">
        <Avatar url={card.author_photo} name={card.author} />
        <div className="cf-card__who">
          <span className="cf-card__name">{card.author || t('feedSomeone')}</span>
          <span className="cf-card__when">{shortDate(card.created_at, i18n.language)}</span>
        </div>
        <div className="cf-card__tools">
          {canModerate && card.kind !== 'event_published' && card.company_id && (
            <button
              type="button"
              className="cf-tool"
              disabled={busy}
              onClick={() => act(apiRequest('POST', at(`/company/feed/${card.id}/pin`),
                                            { pinned: !card.pinned }))}
              aria-label={card.pinned ? t('feedUnpin') : t('feedPin')}
              title={card.pinned ? t('feedUnpin') : t('feedPin')}
            >
              <Icon name="thumbtack" />
            </button>
          )}
          {canRemove(card) && (
            <button
              type="button"
              className="cf-tool"
              disabled={busy}
              onClick={() => {
                // ★ The announcement asks its own question. "Take this off the feed?" over an
                // event is exactly the moment an admin wonders whether they are cancelling it.
                // eslint-disable-next-line no-alert
                if (!window.confirm(card.kind === 'event_published'
                  ? t('feedRemoveEventConfirm') : t('feedRemoveConfirm'))) return;
                act(apiRequest('DELETE', at(`/company/feed/${card.id}`)));
              }}
              aria-label={t('remove')}
              title={t('remove')}
            >
              <Icon name="trash" />
            </button>
          )}
          {/* ★ Not a weaker bin. The admin decides what belongs on this company's stream; a
              report says the content itself is over a line, which is ours to judge and not the
              customer's — so it removes nothing and goes to a queue in the back office. */}
          {card.author_id !== me && card.kind !== 'event_published' && (
            <button
              type="button"
              className="cf-tool"
              disabled={busy}
              onClick={() => setReporting(reporting === card.id ? null : card.id)}
              aria-label={t('report')}
              title={t('report')}
            >
              {/* Icon-only here, unlike the photo lightbox: this bar sits in the corner of every
                  card in a scrolling stream, and a word on each of them would read as a column
                  of "Report" down the page. The tooltip carries it, and tapping opens the panel
                  that explains what a report does before anything is sent. */}
              <Icon name="flag" />
            </button>
          )}
        </div>
      </div>

      {reporting === card.id && (
        <div className="cf-report">
          <p className="cf-card__when">{t('reportExplains')}</p>
          <select className="cf-input" value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}>
            {REPORT_REASONS.map((r) => (
              <option key={r} value={r}>{t(`reportReason_${r}`)}</option>
            ))}
          </select>
          <textarea
            className="cf-input"
            rows={2}
            maxLength={500}
            value={reportNote}
            placeholder={t('reportNotePlaceholder')}
            onChange={(e) => setReportNote(e.target.value)}
          />
          <button type="button" className="bravos-submit" disabled={busy}
                  onClick={() => sendReport(card.id)}>
            {t('reportSend')}
          </button>
          <button type="button" className="cf-tool" onClick={() => setReporting(null)}>
            {t('cancel')}
          </button>
        </div>
      )}

      {cardBody(card)}
      {social(card)}
    </Card>
  );

  return (
    /* "feed", not "home": under Bravos this screen IS the nav's feed (NavBar), so the left icon
       is the one that lights up. The Back arrow still goes to the member home. */
    <Frame {...frameProps}>
      <Card>
        {!clubId && (<>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="megaphone" theme="primary" className="me-2" />
          {t('feedTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('feedNote')}</p>
        </>)}

        {/* A top-level post belongs to a company's feed, so somebody without one reads and
            replies but does not compose. */}
        {hasCompany && canPost && (<>
        <textarea
          className="cf-input"
          rows={3}
          maxLength={MAX_BODY}
          value={draft}
          placeholder={t('feedPlaceholder')}
          onChange={(e) => setDraft(e.target.value)}
        />

        {/* ★ What the post carries beside its words: a picture, and ONE thing it points at. Chips
            rather than three open pickers — the box is for writing, and a composer that opens as
            a form gets filled in like a form. A picker is only offered when there is something in
            it, the same rule the filters below obey. */}
        <div className="cf-attach">
          <label className={`cf-chip ${photo ? 'is-on' : ''}`}>
            <Icon name="camera" className="me-1" />{t('feedAddPhoto')}
            <input
              type="file"
              accept="image/*"
              className="cf-attach__file"
              disabled={busy}
              onChange={(e) => {
                setPhoto((e.target.files || [])[0] || null);
                e.target.value = '';
              }}
            />
          </label>
          {targetSurveys.length > 0 && (
            <button
              type="button"
              className={`cf-chip ${link && link.kind === 'survey' ? 'is-on' : ''}`}
              onClick={() => setAttaching(attaching === 'survey' ? null : 'survey')}
            >
              <Icon name="comment-question" className="me-1" />{t('feedAddSurvey')}
            </button>
          )}
          {targetEvents.length > 0 && (
            <button
              type="button"
              className={`cf-chip ${link && link.kind === 'event' ? 'is-on' : ''}`}
              onClick={() => setAttaching(attaching === 'event' ? null : 'event')}
            >
              <Icon name="calendar" className="me-1" />{t('feedAddEvent')}
            </button>
          )}
        </div>

        {/* One link, not a list: picking a second replaces the first, because "have a look at
            this" is one thing and a post that carries three is a newsletter. */}
        {attaching && (
          <select
            className="cf-select cf-attach__pick"
            value={link && link.kind === attaching ? link.id : ''}
            onChange={(e) => {
              const chosen = (attaching === 'survey' ? targetSurveys : targetEvents)
                .find((o) => o.id === e.target.value);
              setLink(chosen ? { ...chosen, kind: attaching } : null);
              setAttaching(null);
            }}
          >
            <option value="">
              {attaching === 'survey' ? t('feedPickSurvey') : t('feedPickEvent')}
            </option>
            {(attaching === 'survey' ? targetSurveys : targetEvents).map((o) => (
              <option key={o.id} value={o.id}>
                {o.date ? `${o.name} · ${shortDate(o.date, i18n.language)}` : o.name}
              </option>
            ))}
          </select>
        )}

        {(photo || link) && (
          <div className="cf-attached">
            {photo && (
              <span className="cf-attached__item">
                <Icon name="camera" className="me-1" />{photo.name}
                <button type="button" className="cf-attached__off" aria-label={t('remove')}
                        onClick={() => setPhoto(null)}>
                  <Icon name="cross" />
                </button>
              </span>
            )}
            {link && (
              <span className="cf-attached__item">
                <Icon name={link.kind === 'survey' ? 'comment-question' : 'calendar'}
                      className="me-1" />{link.name}
                <button type="button" className="cf-attached__off" aria-label={t('remove')}
                        onClick={() => { setLink(null); setAttaching(null); }}>
                  <Icon name="cross" />
                </button>
              </span>
            )}
          </div>
        )}

        <button
          type="button"
          className="bravos-submit"
          disabled={busy || (!draft.trim() && !photo && !link)}
          onClick={post}
        >
          <Icon name="paper-plane" /> {t('feedPost')}
        </button>
        </>)}
        {/* ★ The server refuses the write anyway (mutations/social is the choke point). This is
            so a restricted person reads WHY here, rather than after pressing Post. */}
        {hasCompany && !canPost && (
          <p className="bravos-cal__notice">
            {t('postingRestricted')}
            {data.post_block_reason ? ` ${data.post_block_reason}` : ''}
          </p>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      <Card>
        {/* ONE row: what to show on the left, the way across to the wall at the right-hand end of
            it. Two filters, and both only when there is something to choose — "only our content"
            appears for somebody who HAS a company, and the event dropdown offers only events this
            person already has cards for. */}
        <div className="cf-filters">
          {hasCompany && !clubId && (
            <>
              <button type="button"
                      className={`cf-chip ${!scope ? 'is-on' : ''}`}
                      onClick={() => setScope(null)}>{t('feedAll')}</button>
              <button type="button"
                      className={`cf-chip ${scope === 'company' ? 'is-on' : ''}`}
                      onClick={() => setScope('company')}>{t('feedOnlyOurs')}</button>
              <button type="button"
                      className={`cf-chip ${scope === 'labs' ? 'is-on' : ''}`}
                      onClick={() => setScope('labs')}>{t('feedOnlyLabs')}</button>
            </>
          )}
          {feedEvents.length > 1 && (
            <select className="cf-select"
                    value={eventId || ''}
                    onChange={(e) => setEventId(e.target.value || null)}>
              <option value="">{t('feedAllEvents')}</option>
              {feedEvents.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          )}
          {/* The wall (F8): the same cards, only the pictures, and ordered by what people liked
              rather than by when it happened. */}
          {!clubId && (
          <button type="button" className="cf-chip cf-chip--wall"
                  onClick={() => navigate('/photo-wall')}>
            <Icon name="camera" className="me-1" />{t('photoWallTitle')}
          </button>
          )}
        </div>
      </Card>

      {pinned.map(renderCard)}
      {items.map(renderCard)}

      {pinned.length === 0 && items.length === 0 && (
        <Card><p className="ce-note">{t('feedEmpty')}</p></Card>
      )}

      {hasMore && (
        <Card>
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            disabled={busy}
            onClick={loadMore}
          >
            {t('showMore')}
          </button>
        </Card>
      )}
    </Frame>
  );
};

export default CompanyFeed;
